import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { AccountInfo } from '@azure/msal-browser';
import { msalInstance, tokenRequest } from './msalConfig';
import { adminApiService } from '../services/api';

interface AuthContextType {
  user: AccountInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  accessToken: string | null;
  roles: string[];
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // MSAL is already initialized and redirect-handled before React mounts (see index.tsx).
  // Active account is set in index.tsx from redirect response or cache.
  const initialAccount = msalInstance.getActiveAccount();

  const [user, setUser] = useState<AccountInfo | null>(initialAccount);
  const [isAuthenticated, setIsAuthenticated] = useState(!!initialAccount);
  const [isLoading, setIsLoading] = useState(!!initialAccount); // Only loading if we need to acquire a token
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const refreshingToken = useRef<Promise<string | null> | null>(null);

  const extractRoles = useCallback((token: string): string[] => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.roles || [];
    } catch {
      return [];
    }
  }, []);

  const hasRole = useCallback((role: string): boolean => {
    return roles.includes(role);
  }, [roles]);

  const hasAnyRole = useCallback((checkRoles: string[]): boolean => {
    return checkRoles.some(role => roles.includes(role));
  }, [roles]);

  // Acquire initial token on mount if we have an account
  useEffect(() => {
    if (!initialAccount) return;

    let cancelled = false;

    (async () => {
      try {
        const response = await msalInstance.acquireTokenSilent({
          ...tokenRequest,
          account: initialAccount,
        });
        if (cancelled) return;
        setAccessToken(response.accessToken);
        setRoles(extractRoles(response.accessToken));
        try {
          localStorage.setItem('adminToken', response.accessToken);
        } catch {
          // ignore
        }
      } catch (error) {
        console.warn('Could not acquire token silently:', error);
        // Account exists but token acquisition failed - session may be expired
        if (!cancelled) {
          setUser(null);
          setIsAuthenticated(false);
          localStorage.removeItem('adminToken');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async () => {
    try {
      setIsLoading(true);
      await msalInstance.loginRedirect({
        scopes: [`api://${process.env.REACT_APP_AZURE_CLIENT_ID}/access_as_user`],
      });
    } catch (error) {
      console.error('Login failed:', error);
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      const account = msalInstance.getAllAccounts()[0];
      if (account) {
        await msalInstance.clearCache({ account });
      } else {
        await msalInstance.clearCache();
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setIsAuthenticated(false);
    setAccessToken(null);
    setRoles([]);
    localStorage.removeItem('adminToken');
  };

  const isTokenExpired = useCallback((token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp - now < 300;
    } catch {
      return true;
    }
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!user) return null;

    if (accessToken && !isTokenExpired(accessToken)) {
      return accessToken;
    }

    if (refreshingToken.current) {
      return await refreshingToken.current;
    }

    refreshingToken.current = (async () => {
      try {
        const response = await msalInstance.acquireTokenSilent({
          ...tokenRequest,
          account: user,
        });

        setAccessToken(response.accessToken);
        setRoles(extractRoles(response.accessToken));

        try {
          localStorage.setItem('adminToken', response.accessToken);
        } catch {
          // ignore
        }

        return response.accessToken;
      } catch (error) {
        console.error('Failed to acquire token silently:', error);

        setAccessToken(null);
        setRoles([]);
        localStorage.removeItem('adminToken');
        setUser(null);
        setIsAuthenticated(false);

        return null;
      } finally {
        refreshingToken.current = null;
      }
    })();

    return await refreshingToken.current;
  }, [user, accessToken, isTokenExpired, extractRoles]);

  useEffect(() => {
    adminApiService.setTokenProvider(async () => {
      return await getAccessToken();
    });
  }, [getAccessToken]);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    accessToken,
    roles,
    hasRole,
    hasAnyRole,
    login,
    logout,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
